import {
  LocaleKey,
  RuntimeLocaleKey,
  useFormatMessage,
  useFormatRichMessage2,
} from "../intl";
import { array, boolean, eq, option, ord, taskEither } from "fp-ts";
import {
  Body,
  Box,
  Button,
  Card,
  Dialog,
  Divider,
  Heading,
  InfoIcon,
  LoadingButton,
  LocalizedString,
  Space,
  Stack,
  TextChildren,
  unsafeLocalizedString,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { constant, constTrue, constVoid, pipe } from "fp-ts/function";
import { useAppContext } from "../useAppContext";
import { useBranchExperienceContext } from "../BranchExperience/BranchExperienceContext";
import { useShareWithClientButtonProps } from "../Common/ShareWithClientButton/useShareWithClientButtonProps";
import { useEffect, useState } from "react";
import { foldChannelLocation } from "../globalDomain";
import { useCommand } from "../useAPI";
import * as documentAPI from "../Common/documentAPI";
import { usePortalStatusContext } from "../PortalStatusContext";
import * as classes from "./PackagesSelection.css";
import { FilePDFDialog } from "../Common/Dialogs/FilePDFDialog/FilePDFDialog";
import cx from "classnames";
import DebitCardBasic from "./basic.png";
import DebitCardMiddle from "./middle.png";
import DebitCardUpper from "./upper.png";
import { InfoTooltip } from "../Common/InfoTooltip/InfoTooltip";
import { palette } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { PackageType, UKontoPackage } from "./api";
import { State } from "./state";
import { PackageFeatureGroup } from "./PackageFeatureGroup";
import { PackagePrice } from "./PackagePrice";
import { useIsSBChannel } from "../useChannel";

const foldPackageType: <T>(match: {
  whenBasic: () => T;
  whenMiddle: () => T;
  whenUpper: () => T;
}) => (type: PackageType) => T = match => type => {
  switch (type) {
    case "BASIC":
      return match.whenBasic();
    case "MIDDLE":
      return match.whenMiddle();
    case "UPPER":
      return match.whenUpper();
  }
};

type PackageCardVariant = "default" | "suggested";

type PackageCardProps = {
  variant: PackageCardVariant;
  data: UKontoPackage;
  currentState: State;
  onConfirm: option.Option<
    (ukontoPackage: PackageType) => taskEither.TaskEither<void, void>
  >;
  onChoose: (v: option.Option<PackageType>) => unknown;
  onSharing: (v: boolean) => unknown;
  isCf?: boolean;
  showMastercard: boolean;
};

export const PackageCard = ({
  variant,
  data,
  currentState,
  onConfirm,
  onChoose,
  onSharing,
  isCf,
  showMastercard,
}: PackageCardProps) => {
  const isSb = useIsSBChannel();
  const formatMessage = useFormatMessage();
  const formatRichMessage = useFormatRichMessage2();
  const optionalFormatMessage = (
    id: LocaleKey
  ): option.Option<LocalizedString> =>
    pipe(
      eq.eqString.equals(formatMessage(id), id),
      boolean.fold(
        () => option.some(formatMessage(id)),
        () => option.none
      )
    );
  const optionalRichFormatMessage = (
    id: LocaleKey
  ): option.Option<TextChildren> =>
    pipe(
      eq.eqString.equals(formatMessage(id), id),
      boolean.fold(
        () => option.some(formatRichMessage(id, undefined, { fromSb: isSb })),
        () => option.none
      )
    );
  const {
    apiParameters: { channel },
    config: {
      packageSelectionOpenMastercardImgUrl,
      packageSelectionMiddleMastercardImgUrl,
      packageSelectionTopMastercardImgUrl,
      cashLoanStartPackageOnly,
    },
  } = useAppContext();

  const onlyStartCF = isCf && cashLoanStartPackageOnly;
  const { branchExperienceFeaturesActive } = useBranchExperienceContext();
  const shouldShare = pipe(
    currentState.sharedWithClient,
    boolean.fold(constTrue, () =>
      pipe(
        currentState.package,
        option.exists(item => item !== data.type)
      )
    )
  );
  const defaultAction = pipe(
    onConfirm,
    option.fold(
      () => taskEither.fromIO<unknown, unknown>(constVoid),
      func => func(data.type)
    )
  );

  const sharedWithClientSubmitButtonProps = useShareWithClientButtonProps({
    disabled: false,
    action: shouldShare
      ? taskEither.fromIO<void, void>(() => onChoose(option.some(data.type)))
      : defaultAction,

    submitLabel: formatMessage("Confirm.buttonLabel"),
    branchExperienceState:
      !branchExperienceFeaturesActive ||
      pipe(
        currentState.package,
        option.exists(item => item === data.type)
      )
        ? "sharedWithClient"
        : "notShared",
    shareWithClientLabel: formatMessage(
      "UKontoFirstPart.UKontoPackagesSelection.action.choose"
    ),
  });

  useEffect(() => {
    onSharing(!sharedWithClientSubmitButtonProps.disabled);
  }, [sharedWithClientSubmitButtonProps.disabled]);

  const regularSubmitButtonProps = {
    action: defaultAction,
    labels: {
      normal: formatMessage(
        onlyStartCF
          ? "CF.PackagesSelection.action.choose"
          : "UKontoFirstPart.UKontoPackagesSelection.action.choose"
      ),
      loading: formatMessage(
        onlyStartCF
          ? "CF.PackagesSelection.action.choose"
          : "UKontoFirstPart.UKontoPackagesSelection.action.choose"
      ),
      error: formatMessage(
        onlyStartCF
          ? "CF.PackagesSelection.action.choose"
          : "UKontoFirstPart.UKontoPackagesSelection.action.choose"
      ),
      success: formatMessage(
        onlyStartCF
          ? "CF.PackagesSelection.action.choose"
          : "UKontoFirstPart.UKontoPackagesSelection.action.choose"
      ),
    },
  };

  const submitButtonProps = pipe(
    channel,
    foldChannelLocation({
      Remote: constant(regularSubmitButtonProps),
      InPerson: branchExperienceFeaturesActive
        ? constant(sharedWithClientSubmitButtonProps)
        : constant(regularSubmitButtonProps),
    })
  );

  const isChosen = pipe(
    currentState.package,
    option.fold(
      () => false,
      p =>
        pipe(
          eq.eqString.equals(p, data.type),
          boolean.fold(
            () => false,
            () => true
          )
        )
    )
  );

  const lowerCaseType = data.type.toLowerCase();

  const moreLinkSuffix = ".more_info";
  const tooltipSuffix = ".tooltip";
  const priceAdditionalInfoSuffix = ".additional.info";
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [showMoreInfoMain, setShowMoreInfoMain] = useState(false);
  const insuranceInfoTemplate = useCommand(documentAPI.templates);
  const insuranceInfoContent = useCommand(documentAPI.content);
  const { portalBlocked } = usePortalStatusContext();
  const isMobileLayout = useIsMobileLayout();

  const imgAltMessage = pipe(
    data.type,
    foldPackageType({
      whenBasic: () => formatMessage("PackagesSelection.mastercard.start.alt"),
      whenMiddle: () => formatMessage("PackagesSelection.mastercard.open.alt"),
      whenUpper: () => formatMessage("PackagesSelection.mastercard.top.alt"),
    })
  );

  return (
    <Box
      basis={"330px"}
      grow
      shrink
      className={
        isMobileLayout ? classes.cardMaxWidthMobile : classes.cardMaxWidth
      }
    >
      {showMoreInfo && (
        <FilePDFDialog
          onDismiss={() => setShowMoreInfo(false)}
          title={formatMessage(
            "UKontoFirstPart.UKontoPackagesSelection.Document.title"
          )}
          file={pipe(
            insuranceInfoTemplate({
              docsets: ["PackageInsuranceInfo"],
            }),
            taskEither.chain(items =>
              pipe(
                insuranceInfoContent({ docId: items[0].docId }),
                taskEither.map(value => ({
                  ...value,
                  downloadUrl: items[0].downloadUrl,
                }))
              )
            )
          )}
        />
      )}
      {showMoreInfoMain && (
        <Dialog
          variant="center"
          size="medium"
          title={formatMessage(
            `${
              onlyStartCF ? "CF." : ""
            }UKontoFirstPart.UKontoPackagesSelection.MainDialog.title.${
              data.type
            }` as LocaleKey
          )}
          onDismiss={option.some(() => setShowMoreInfoMain(false))}
          actions={[]}
          icon={InfoIcon}
        >
          <Box column shrink style={{ marginLeft: "6px", marginRight: "6px" }}>
            <Body size={"medium"} weight={"regular"}>
              {formatRichMessage(
                `${
                  onlyStartCF ? "CF." : ""
                }UKontoFirstPart.UKontoPackagesSelection.MainDialog.body.${
                  data.type
                }.1` as LocaleKey,
                undefined,
                { fromSb: isSb }
              )}
              {formatRichMessage(
                `${
                  onlyStartCF ? "CF." : ""
                }UKontoFirstPart.UKontoPackagesSelection.MainDialog.body.${
                  data.type
                }.2` as LocaleKey,
                undefined,
                { fromSb: isSb }
              )}
              {formatRichMessage(
                `${
                  onlyStartCF ? "CF." : ""
                }UKontoFirstPart.UKontoPackagesSelection.MainDialog.body.${
                  data.type
                }.3` as LocaleKey,
                undefined,
                { fromSb: isSb }
              )}
              {formatRichMessage(
                `${
                  onlyStartCF ? "CF." : ""
                }UKontoFirstPart.UKontoPackagesSelection.MainDialog.body.${
                  data.type
                }.4` as LocaleKey,
                undefined,
                { fromSb: isSb }
              )}
            </Body>
          </Box>
        </Dialog>
      )}
      <Card
        grow
        highlighted={variant === "suggested" || isChosen}
        badge={
          variant === "suggested"
            ? {
                variant: "label",
                label: formatMessage(
                  "UKontoFirstPart.UKontoPackagesSelection.badge"
                ),
              }
            : undefined
        }
      >
        <Box
          shrink
          column
          className={cx(
            classes.card[variant],
            isChosen && classes.chosenCard,
            isMobileLayout && classes.cardMaxWidthMobile
          )}
        >
          <Stack column className={classes.header} units={2}>
            <Heading
              size="medium"
              weight="medium"
              align="center"
              className={classes.headerHeading}
            >
              {formatMessage(
                `${
                  onlyStartCF ? "CF." : ""
                }package.${lowerCaseType}.name` as LocaleKey
              )}
            </Heading>
            <Body
              size="x-small"
              weight="medium"
              align="center"
              className={classes.headerBody}
            >
              {formatMessage(
                onlyStartCF
                  ? "CF.PackagesSelection.header.subtitle"
                  : "UKontoFirstPart.UKontoPackagesSelection.header.subtitle"
              )}
            </Body>

            <Box hAlignContent="center">
              <img
                src={pipe(
                  data.type,
                  foldPackageType({
                    whenBasic: () =>
                      showMastercard
                        ? packageSelectionOpenMastercardImgUrl
                        : DebitCardBasic,
                    whenMiddle: () =>
                      showMastercard
                        ? packageSelectionMiddleMastercardImgUrl
                        : DebitCardMiddle,
                    whenUpper: () =>
                      showMastercard
                        ? packageSelectionTopMastercardImgUrl
                        : DebitCardUpper,
                  })
                )}
                alt={imgAltMessage}
                aria-label={imgAltMessage}
                role={"img"}
                className={
                  showMastercard ? classes.debitCardMc : classes.debitCard
                }
              />
            </Box>
            <Body
              size="x-small"
              weight="medium"
              align="center"
              className={classes.headerBody}
            >
              {formatMessage(
                onlyStartCF
                  ? "CF.PackagesSelection.header.caption"
                  : "UKontoFirstPart.UKontoPackagesSelection.header.caption"
              )}
            </Body>
            <Body
              size="medium"
              weight="regular"
              align="center"
              className={classes.headerCaption}
            >
              {formatMessage(
                `${
                  onlyStartCF ? "CF." : ""
                }package.${lowerCaseType}.description` as LocaleKey
              )}
            </Body>
          </Stack>

          <Box grow column className={classes.body}>
            <Stack column grow shrink units={6}>
              {pipe(
                Object.keys(data.featuresByGroup),
                array.sort(ord.ordString),
                array.filter(k => !onlyStartCF || k !== "group_2"),
                array.mapWithIndex((i, k) => (
                  <>
                    <PackageFeatureGroup
                      key={k}
                      label={formatMessage(
                        `${
                          onlyStartCF ? "CF." : ""
                        }package.${lowerCaseType}.${k}` as RuntimeLocaleKey
                      )}
                      features={pipe(
                        data.featuresByGroup[k],
                        array.map(f => ({
                          variant: f.included ? "included" : "not-included",
                          label: formatRichMessage(
                            ((onlyStartCF ? "CF." : "") +
                              f.description) as RuntimeLocaleKey
                          ),
                          info: pipe(
                            optionalFormatMessage(
                              `${onlyStartCF ? "CF." : ""}${
                                f.description
                              }${tooltipSuffix}` as RuntimeLocaleKey
                            ),
                            option.map(msg => (
                              <InfoTooltip content={msg} size="x-small" />
                            ))
                          ),
                        }))
                      )}
                      info={pipe(
                        optionalFormatMessage(
                          `package.${lowerCaseType}.${k}${moreLinkSuffix}` as RuntimeLocaleKey
                        ),
                        option.map(label => (
                          <div
                            aria-label={formatMessage(
                              "package.button.moreinfo"
                            )}
                            role={"button"}
                            onClick={() => setShowMoreInfo(true)}
                          >
                            <Button
                              ariaLabel={formatMessage(
                                "package.button.moreinfo"
                              )}
                              label={label}
                              variant="text"
                              size="small"
                              action={() => setShowMoreInfo(true)}
                            />
                          </div>
                        ))
                      )}
                    />

                    {i < Object.keys(data.featuresByGroup).length - 1 && (
                      <>
                        <Space units={3} />
                        <Divider color={palette.neutral400} />
                      </>
                    )}
                  </>
                ))
              )}
              <Space fluid />
              <Stack column units={1}>
                <Button
                  variant="text"
                  label={formatMessage(
                    `${
                      onlyStartCF ? "CF." : ""
                    }UKontoFirstPart.UKontoPackagesSelection.MainDialog.MoreInfo.${
                      data.type
                    }` as LocaleKey
                  )}
                  size="default"
                  action={() => setShowMoreInfoMain(true)}
                />
                <Space units={2}></Space>
                {pipe(
                  onConfirm,
                  option.fold(
                    () => <></>,
                    () => (
                      <Box
                        shrink
                        basis="0%"
                        className={classes.buttonContainer}
                        hAlignContent="center"
                      >
                        <LoadingButton
                          ariaLabel={pipe(
                            currentState.package,
                            option.fold(
                              () =>
                                formatMessage(
                                  "UKontoFirstPart.UKontoPackagesSelection.action.choose.altText"
                                ),
                              t =>
                                pipe(
                                  eq.eqString.equals(t, data.type),
                                  boolean.fold(
                                    () =>
                                      formatMessage(
                                        "UKontoFirstPart.UKontoPackagesSelection.action.choose.altText"
                                      ),
                                    () =>
                                      formatMessage(
                                        "UKontoFirstPart.UKontoPackagesSelection.action.confirm.altText"
                                      )
                                  )
                                )
                            )
                          )}
                          type="submit"
                          variant={pipe(
                            currentState.package,
                            option.fold(
                              () => "primary",
                              t =>
                                pipe(
                                  eq.eqString.equals(t, data.type),
                                  boolean.fold(
                                    () => "secondary",
                                    () => "primary"
                                  )
                                )
                            )
                          )}
                          size="small"
                          {...submitButtonProps}
                          disabled={
                            (pipe(
                              currentState.package,
                              option.exists(item => item !== data.type)
                            ) &&
                              !currentState.sharedWithClient &&
                              !isCf) ||
                            !!sharedWithClientSubmitButtonProps.disabled ||
                            portalBlocked
                          }
                          className={classes.button}
                        />
                      </Box>
                    )
                  )
                )}
                <PackagePrice
                  amountIntPart={formatMessage(
                    `${
                      onlyStartCF ? "CF." : ""
                    }package.${lowerCaseType}.price.amount_int_part` as LocaleKey
                  )}
                  amountDecPart={pipe(
                    optionalFormatMessage(
                      `${
                        onlyStartCF ? "CF." : ""
                      }package.${lowerCaseType}.price.amount_dec_part` as LocaleKey
                    ),
                    option.toUndefined
                  )}
                  currency={formatMessage(
                    `${
                      onlyStartCF ? "CF." : ""
                    }package.${lowerCaseType}.price.currency` as LocaleKey
                  )}
                  info={pipe(
                    optionalRichFormatMessage(
                      `${
                        onlyStartCF ? "CF." : ""
                      }package.${lowerCaseType}.price${priceAdditionalInfoSuffix}` as LocaleKey
                    )
                  )}
                  tooltip={pipe(
                    optionalFormatMessage(
                      `${
                        onlyStartCF ? "CF." : ""
                      }package.${lowerCaseType}.price.periodicity${tooltipSuffix}` as LocaleKey
                    ),
                    option.map(msg => (
                      <InfoTooltip content={msg} size="x-small" />
                    ))
                  )}
                  periodicity={formatMessage(
                    `${
                      onlyStartCF ? "CF." : ""
                    }package.${lowerCaseType}.price.periodicity` as LocaleKey
                  )}
                  before_discount_amount_int_part={pipe(
                    optionalFormatMessage(
                      `${
                        onlyStartCF ? "CF." : ""
                      }package.${lowerCaseType}.price.before_discount_amount_int_part` as LocaleKey
                    ),
                    option.map(msg => unsafeLocalizedString(msg.trim())),
                    option.toUndefined
                  )}
                  before_discount_amount_dec_part={pipe(
                    optionalFormatMessage(
                      `${
                        onlyStartCF ? "CF." : ""
                      }package.${lowerCaseType}.price.before_discount_amount_dec_part` as LocaleKey
                    ),
                    option.map(msg => unsafeLocalizedString(msg.trim())),
                    option.toUndefined
                  )}
                  before_discount_currency={pipe(
                    optionalFormatMessage(
                      `${
                        onlyStartCF ? "CF." : ""
                      }package.${lowerCaseType}.price.before_discount_currency` as LocaleKey
                    ),
                    option.getOrElse(() =>
                      formatMessage(
                        `${
                          onlyStartCF ? "CF." : ""
                        }package.${lowerCaseType}.price.currency` as LocaleKey
                      )
                    )
                  )}
                />
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
